import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge, CRIT_TONE } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain, Sparkle, Wrench, ListChecks, MagnifyingGlass, Package } from "@phosphor-icons/react";
import { toast } from "sonner";

const KAT_TONE = { wartung: "blue", pruefung: "amber", reparatur: "amber", notdienst: "red", service: "blue" };

export default function ServiceAI() {
  const [assets, setAssets] = useState([]);
  const [assetId, setAssetId] = useState("");
  const [desc, setDesc] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/assets").then((r) => setAssets(r.data)); }, []);

  const analyze = async () => {
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/classify", { description: desc, asset_id: assetId || null });
      setResult(data);
      toast.success("KI-Analyse abgeschlossen");
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
    finally { setLoading(false); }
  };

  const List = ({ icon: Icon, title, items }) => (
    <Card className="rounded-md border p-5">
      <div className="mb-3 flex items-center gap-2"><Icon size={18} weight="duotone" className="text-primary" /><Label2>{title}</Label2></div>
      <ul className="space-y-1.5 text-sm">
        {(items || []).length === 0 && <li className="text-muted-foreground">—</li>}
        {(items || []).map((x, i) => <li key={i} className="flex gap-2"><span className="text-primary">›</span>{x}</li>)}
      </ul>
    </Card>
  );

  return (
    <div>
      <PageHeader title="KI-Analyse" subtitle="Störungsbeschreibung klassifizieren · Priorität · Ursachen · Maßnahmen" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="rounded-md border p-6">
          <div className="mb-4 flex items-center gap-2"><Brain size={22} weight="duotone" className="text-primary" /><h2 className="font-heading text-lg font-bold">Störung beschreiben</h2></div>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label2>Anlage (optional)</Label2>
              <Select value={assetId || "none"} onValueChange={(v) => setAssetId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="ai-asset"><SelectValue placeholder="Keine" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Keine</SelectItem>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label2>Beschreibung *</Label2>
              <Textarea data-testid="ai-description" rows={6} value={desc} onChange={(e) => setDesc(e.target.value)}
                placeholder="z.B. Sektionaltor fährt nur noch ruckartig, lautes Schleifgeräusch, bleibt auf halber Höhe stehen." />
            </div>
            <Button data-testid="ai-analyze-button" className="w-full gap-2" onClick={analyze} disabled={!desc.trim() || loading}>
              <Sparkle size={18} weight="bold" />{loading ? "Analysiere…" : "KI-Analyse starten"}
            </Button>
          </div>
        </Card>

        <div className="space-y-4">
          {!result && !loading && (
            <Card className="flex h-full min-h-[300px] flex-col items-center justify-center rounded-md border border-dashed p-8 text-center text-muted-foreground">
              <Brain size={40} weight="duotone" className="mb-3 opacity-50" />
              <p className="text-sm">Ergebnis der KI-Klassifizierung erscheint hier.</p>
            </Card>
          )}
          {loading && <Card className="flex h-full min-h-[300px] items-center justify-center rounded-md border p-8 text-sm text-muted-foreground">KI arbeitet…</Card>}
          {result && (
            <div className="animate-fade-in space-y-4" data-testid="ai-result">
              <Card className="rounded-md border p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone={KAT_TONE[result.kategorie] || "blue"}>Kategorie: {result.kategorie}</StatusBadge>
                  <StatusBadge tone={CRIT_TONE[result.prioritaet] || "blue"}>Priorität: {result.prioritaet}</StatusBadge>
                  {result.first_time_fix_wahrscheinlich !== undefined && (
                    <StatusBadge tone={result.first_time_fix_wahrscheinlich ? "green" : "gray"}>
                      First-Time-Fix: {result.first_time_fix_wahrscheinlich ? "wahrscheinlich" : "unwahrscheinlich"}
                    </StatusBadge>
                  )}
                </div>
                {result.zusammenfassung && <p className="mt-3 text-sm">{result.zusammenfassung}</p>}
              </Card>
              <List icon={Wrench} title="Empfohlene Maßnahmen" items={result.empfohlene_massnahmen} />
              <List icon={MagnifyingGlass} title="Mögliche Ursachen" items={result.moegliche_ursachen} />
              <List icon={Package} title="Benötigtes Material" items={result.benoetigtes_material} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
